import type {
  DerivedExportDateRange,
  ExportDateOptionId,
  ExportDateRangeSelection,
} from '../models/export.types'

export interface ExportDateOption {
  readonly id: ExportDateOptionId
  readonly labelKey: string
  readonly amountUnitKey?: string
}

export const DATE_RANGE_OPTIONS: readonly ExportDateOption[] = Object.freeze([
  { id: 'yesterday', labelKey: 'dataExport.dateRange.options.yesterday' },
  { id: 'this_week', labelKey: 'dataExport.dateRange.options.thisWeek' },
  { id: 'last_week', labelKey: 'dataExport.dateRange.options.lastWeek' },
  { id: 'last_7_days', labelKey: 'dataExport.dateRange.options.last7Days' },
  { id: 'last_14_days', labelKey: 'dataExport.dateRange.options.last14Days' },
  { id: 'this_month', labelKey: 'dataExport.dateRange.options.thisMonth' },
  { id: 'last_month', labelKey: 'dataExport.dateRange.options.lastMonth' },
  {
    id: 'last_x_days',
    labelKey: 'dataExport.dateRange.options.lastXDays',
    amountUnitKey: 'dataExport.dateRange.units.days',
  },
  {
    id: 'last_x_weeks',
    labelKey: 'dataExport.dateRange.options.lastXWeeks',
    amountUnitKey: 'dataExport.dateRange.units.weeks',
  },
  { id: 'custom', labelKey: 'dataExport.dateRange.options.custom' },
])

export const RANGE_LIMITS = Object.freeze({ days: 90, weeks: 12 })

/**
 * Pure replacement for the legacy pair of immediate/deep watchers. A selection and a clock value
 * produce the complete API-facing date range in one pass, including `data_fetch_days`.
 *
 * `Last X Months` is intentionally absent: the live option catalog never exposed it, so its three
 * unreachable branches were dead product code rather than a supported choice.
 */
export function useExportDateRange(
  selection: Readonly<ExportDateRangeSelection>,
  now: Date = new Date(),
): DerivedExportDateRange {
  const today = atLocalNoon(now)
  const yesterday = addDays(today, -1)

  switch (selection.option) {
    case 'yesterday':
      return result(selection.option, yesterday, yesterday, 1)

    case 'this_week': {
      const monday = isoMonday(yesterday)
      return result(selection.option, monday, yesterday, 7)
    }

    case 'last_week': {
      const end = addDays(isoMonday(today), -1)
      return result(selection.option, addDays(end, -6), end, 7)
    }

    case 'last_7_days':
      return result(selection.option, addDays(yesterday, -6), yesterday, 7)

    case 'last_14_days':
      return result(selection.option, addDays(yesterday, -13), yesterday, 14)

    case 'this_month':
      return result(
        selection.option,
        new Date(yesterday.getFullYear(), yesterday.getMonth(), 1, 12),
        yesterday,
        31,
      )

    case 'last_month': {
      const start = new Date(yesterday.getFullYear(), yesterday.getMonth() - 1, 1, 12)
      const end = new Date(yesterday.getFullYear(), yesterday.getMonth(), 0, 12)
      return result(selection.option, start, end, 31)
    }

    case 'last_x_days': {
      const amount = clampAmount(selection.amount, RANGE_LIMITS.days)
      return result(selection.option, addDays(yesterday, -(amount - 1)), yesterday, amount, amount)
    }

    case 'last_x_weeks': {
      const amount = clampAmount(selection.amount, RANGE_LIMITS.weeks)
      const end = addDays(isoMonday(today), -1)
      return result(selection.option, addDays(end, -(amount * 7 - 1)), end, amount * 7, amount)
    }

    case 'custom':
      return {
        option: selection.option,
        firstDate: normalizeCustomDate(selection.customStart),
        lastDate: normalizeCustomDate(selection.customEnd),
        data_fetch_days: 0,
      }
  }
}

function result(
  option: ExportDateOptionId,
  first: Date,
  last: Date,
  dataFetchDays: number,
  amount?: number,
): DerivedExportDateRange {
  const base: DerivedExportDateRange = {
    option,
    firstDate: toLegacyApiDay(first),
    lastDate: toLegacyApiDay(last),
    data_fetch_days: dataFetchDays,
  }
  return amount === undefined ? base : { ...base, amount }
}

function clampAmount(value: number | undefined, max: number): number {
  const finite = typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 1
  return Math.min(Math.max(finite, 1), max)
}

function atLocalNoon(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12)
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

function isoMonday(date: Date): Date {
  const day = date.getDay() === 0 ? 7 : date.getDay()
  return addDays(date, 1 - day)
}

function toLegacyApiDay(date: Date): string {
  const year = String(date.getFullYear())
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}

function normalizeCustomDate(value: string | undefined): string {
  return value?.trim().replaceAll('-', '/') ?? ''
}
